import { useEffect, useState, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import * as moment from "moment";
import CustomTooltip from "components/Custom/CustomTooltip";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import CustomReactDatetime from "components/Custom/CustomReactDatetime.js";
import { getChannel } from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
import { tourGuideSteps } from "views/Clients/Environments/Channels/ChannelSteps";

// react-bootstrap components
import {
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Button,
  Table,
} from "react-bootstrap";
import * as React from "react";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination";
import { AuthContext } from "contexts/authContext";
import { useTour } from "@reactour/tour";

function Channel() {
  // Hooks
  const notify = useContext(NotificationAlertContext);
  const currentUser = useContext(AuthContext);
  const history = useHistory();

  const { clientId, clientEnvironmentId, channelId } = useParams();

  const [startDate, setStartDate] = useState(moment().startOf('day'));
  const [endDate, setEndDate] = useState(moment().endOf('day'));
  const [connectorData, setConnectorData] = useState([]);
  const [channelMetric, setChannelMetric] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  // const [errorMessage, setErrorMessage] = useState(null);

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  const channelHeaders = [
    { key: "channel_id", label: "Channel ID" },
    { key: "channel_name", label: "Channel Name" },
    { key: "received_messages", label: "Received" },
    { key: "queued_messages", label: "Queued" },
    { key: "filtered_messages", label: "Filtered" },
    { key: "errored_messages", label: "Errored" },
    { key: "sent_messages", label: "Sent/Transformed" },
    { key: "weighted_avg_process_time_sec", label: "Total Avg(sec)" },
    {
      key: "weighted_avg_source_finished_to_message_finished_process_time_sec",
      label: "Dest Avg(sec)",
    },
    {
      key: "weighted_avg_message_minus_destination_queues_process_time_sec",
      label: "Total - Dest Q Avg(sec)",
    },
    { key: "weighted_avg_raw_message_size_bytes", label: "Raw Size Avg(KB)" },
    {
      key: "weighted_avg_attachment_size_bytes",
      label: "Attachment Size Avg(KB)",
    },
  ];

  const connectorHeaders = [
    { key: "id", label: "ID" },
    { key: "name", label: "Name" },
    { key: "type", label: "Type" },
    { key: "count", label: "Count" },
    { key: "dispatch_avg", label: "Dispatch Avg" },
  ];

  // Pagination Starts
  const columnMapping = {
    ID: "connector_id",
    Name: "name",
    Type: "type",
    Count: "count",
    "Dispatch Avg": "weighted_avg_dispatch_process_time_sec",
  };

  const [totalCount, setTotalCount] = useState(0);
  const numberOfRowsData = [1000];

  const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[0]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
  const [sortValue, setSortValue] = useState("Position");

  const setStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setCurrentLimit(value);
    } else if (key === "offset") {
      setCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setAscOrDescValue(value);
    } else {
      setSortValue(value);
    }
  };
  // Pagination Ends

  const fetchChannel = async (variables = {}) => {
    try {
      setShowLoader(true);
      const query = {
        clientId,
        channelId,
        clientEnvironmentId,
        startDate,
        endDate,
        sortFilter: variables.sortFilter
          ? variables.sortFilter
          : columnMapping[sortValue],
        sortType: variables.sortType || "ASC",
      };
      const response = await API.graphql(graphqlOperation(getChannel, query));
      const data = response.data;
      if (data?.getChannel?.success) {
        const body = data.getChannel.body;
        setChannelMetric([
          {
            channel_id: body.channel_id,
            channel_name: body.channel_name,
            queued_messages: body.queued_messages,
            received_messages: body.received_messages,
            sent_messages: body.sent_messages,
            errored_messages: body.errored_messages,
            filtered_messages: body.filtered_messages,
            weighted_avg_process_time_sec: body.weighted_avg_process_time_sec,
            weighted_avg_source_finished_to_message_finished_process_time_sec:
              body.weighted_avg_source_finished_to_message_finished_process_time_sec,
            weighted_avg_message_minus_destination_queues_process_time_sec:
              body.weighted_avg_message_minus_destination_queues_process_time_sec,
            weighted_avg_raw_message_size_bytes:
              body.weighted_avg_raw_message_size_bytes,
            weighted_avg_attachment_size_bytes:
              body.weighted_avg_attachment_size_bytes,
          },
        ]);
        setConnectorData(body.connectors);
        setTotalCount(body.connectors.length);
      } else {
        console.error(data?.getChannel?.message);
      }
      return data?.getChannel?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      const filterObj = {
        sortFilter: columnMapping[sortValue],
      };
      const isSuccess = await fetchChannel(filterObj);
      if (!isSuccess) {
        notify("danger");
        history.goBack();
      }
    }
    fetchData();
  }, []);

  if (!connectorData) {
    return <Spinner animation="grow" />;
  }

  if (showLoader) return <Spinner animation="grow" />;

  // if (errorMessage) return errorMessage;

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <h2>Channel Details</h2>
          </Col>
          {currentUser.groupName === "external" && (
            <Col className="my-auto text-right">
              <Button
                variant="secondary"
                className="btn-outline"
                onClick={() => {
                  setSteps(tourGuideSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            </Col>
          )}
        </Row>
        <Row>
          <Col md="12" id="channel-details-header-1">
            <h5>Client Id: {clientId}</h5>
            <h5>Environment Id: {clientEnvironmentId}</h5>
            <h5>Channel Id: {channelId}</h5>
            <h5>Channel Name: {channelMetric[0]?.channel_name}</h5>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <Card.Title>Metrics Search</Card.Title>
              </Card.Header>
              <Card.Body>
                <Row className="align-items-end">
                  <Col md="4" sm="6">
                    <label>Start Date:</label>
                    <CustomReactDatetime
                      inputProps={{
                        className: "form-control",
                        placeholder: "Start Time",
                      }}
                      timeFormat="h:mm A"
                      value={startDate}
                      onChange={setStartDate}
                    ></CustomReactDatetime>
                  </Col>
                  <Col md="4" sm="6">
                    <label>End Date:</label>
                    <CustomReactDatetime
                      inputProps={{
                        className: "form-control",
                        placeholder: "End Time",
                      }}
                      timeFormat="h:mm A"
                      value={endDate}
                      onChange={setEndDate}
                    ></CustomReactDatetime>
                  </Col>
                  <Col md="2" sm="6">
                    <Button
                      variant="primary"
                      onClick={async () => {
                        // Collect validation errors
                        const errors = [];

                        // Validate start date is not after end date
                        if (startDate.isAfter(endDate)) {
                          errors.push("Start date must be lesser than end date");
                        }
                        // Show all errors if any exist
                        if (errors.length > 0) {
                          errors.forEach(error => notify("danger", error));
                          return;
                        }
                        const isSuccess = await fetchChannel({
                          clientId,
                          channelId,
                          clientEnvironmentId,
                          startDate,
                          endDate,
                        });
                        if (isSuccess) notify("success");
                        else notify("danger");
                      }}
                    >
                      {" "}
                      Search
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <Card className="strpied-tabled-with-hover">
              <Card.Header>
                <Card.Title as="h4">Channel Metrics</Card.Title>
              </Card.Header>
              <Card.Body className="table-responsive">
                <ReactTable
                  data={channelMetric}
                  totalCount={channelMetric.length}
                  showTotalCount={false}
                  functionCallBack={fetchChannel}
                  setStateForPagnination={setStateForPagnination}
                  hidePagination={true}
                  currentLimit={currentLimit}
                  currentOffset={currentOffset}
                  ascOrDescValue={ascOrDescValue}
                  sortValue={sortValue}
                  numberOfRowsData={numberOfRowsData}
                  columnMapping={columnMapping}
                  tableName="Channel Metrics"
                  CSVHeaders={channelHeaders}
                  CSVFileName="channe_metric_export"
                  notify={notify}
                  columns={[
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-0"
                            ).content
                          }
                        >
                          Received
                        </CustomTooltip>
                      ),
                      accessor: "received_messages",
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 150,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-1"
                            ).content
                          }
                        >
                          Queued
                        </CustomTooltip>
                      ),
                      accessor: "queued_messages",
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 150,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-2"
                            ).content
                          }
                        >
                          Filtered
                        </CustomTooltip>
                      ),
                      accessor: "filtered_messages",
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 150,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-3"
                            ).content
                          }
                        >
                          Errored
                        </CustomTooltip>
                      ),
                      accessor: "errored_messages",
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 150,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-4"
                            ).content
                          }
                        >
                          Sent/Transformed
                        </CustomTooltip>
                      ),
                      accessor: "sent_messages",
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 150,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-5"
                            ).content
                          }
                        >
                          Total Avg(sec)
                        </CustomTooltip>
                      ),
                      accessor: "weighted_avg_process_time_sec",
                      Cell: (cell) => {
                        const row = cell.row.original;
                        return row.weighted_avg_process_time_sec?.toFixed(3);
                      },
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 200,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-6"
                            ).content
                          }
                        >
                          Dest Avg(sec)
                        </CustomTooltip>
                      ),
                      accessor:
                        "weighted_avg_source_finished_to_message_finished_process_time_sec",
                      Cell: (cell) => {
                        const row = cell.row.original;
                        return row.weighted_avg_source_finished_to_message_finished_process_time_sec?.toFixed(
                          3
                        );
                      },
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 200,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-7"
                            ).content
                          }
                        >
                          Total - Dest Q Avg(sec)
                        </CustomTooltip>
                      ),
                      accessor:
                        "weighted_avg_message_minus_destination_queues_process_time_sec",
                      Cell: (cell) => {
                        const row = cell.row.original;
                        return row.weighted_avg_message_minus_destination_queues_process_time_sec?.toFixed(
                          3
                        );
                      },
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 200,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-8"
                            ).content
                          }
                        >
                          Raw Size Avg(KB)
                        </CustomTooltip>
                      ),
                      accessor: "weighted_avg_raw_message_size_bytes",
                      Cell: (cell) => {
                        const row = cell.row.original;
                        return row.weighted_avg_raw_message_size_bytes?.toFixed(
                          3
                        );
                      },
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 150,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Channel-Metrics-column-9"
                            ).content
                          }
                        >
                          Attachment Size Avg(KB)
                        </CustomTooltip>
                      ),
                      accessor: "weighted_avg_attachment_size_bytes",
                      Cell: (cell) => {
                        const row = cell.row.original;
                        return row.weighted_avg_attachment_size_bytes?.toFixed(
                          3
                        );
                      },
                      filterable: false,
                      showTooltip: false,
                      sortable: false,
                      minWidth: 200,
                    },
                  ]}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col md="12">
            <Card className="strpied-tabled-with-hover">
              <Card.Header>
                <Card.Title as="h4">Connector Metrics</Card.Title>
              </Card.Header>
              <Card.Body className="table-responsive">
                <ReactTable
                  data={connectorData}
                  totalCount={totalCount}
                  functionCallBack={fetchChannel}
                  setStateForPagnination={setStateForPagnination}
                  hidePagination={true}
                  currentLimit={currentLimit}
                  currentOffset={currentOffset}
                  ascOrDescValue={ascOrDescValue}
                  sortValue={sortValue}
                  numberOfRowsData={numberOfRowsData}
                  columnMapping={columnMapping}
                  tableName="Connectors"
                  CSVHeaders={connectorHeaders}
                  CSVFileName="connectors_export"
                  notify={notify}
                  showEmptyTable={true}
                  columns={[
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Connectors-column-0"
                            ).content
                          }
                        >
                          ID
                        </CustomTooltip>
                      ),
                      accessor: "connector_id",
                      filterable: false,
                      showTooltip: false,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Connectors-column-1"
                            ).content
                          }
                        >
                          Name
                        </CustomTooltip>
                      ),
                      accessor: "name",
                      filterable: false,
                      showTooltip: false,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Connectors-column-2"
                            ).content
                          }
                        >
                          Type
                        </CustomTooltip>
                      ),
                      accessor: "type",
                      filterable: false,
                      showTooltip: false,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Connectors-column-3"
                            ).content
                          }
                        >
                          Count
                        </CustomTooltip>
                      ),
                      accessor: "count",
                      filterable: false,
                      showTooltip: false,
                    },
                    {
                      Header: () => (
                        <CustomTooltip
                          placement="left"
                          value={
                            tourGuideSteps.find(
                              (element) =>
                                element.selector === "#Connectors-column-4"
                            ).content
                          }
                        >
                          Dispatch Avg
                        </CustomTooltip>
                      ),
                      accessor: "weighted_avg_dispatch_process_time_sec",
                      Cell: (cell) => {
                        const row = cell.row.original;
                        return row.weighted_avg_dispatch_process_time_sec?.toFixed(
                          3
                        );
                      },
                      filterable: false,
                      showTooltip: false,
                    },
                  ]}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Channel;
